<template>
  <div class="permissoes-acesso-selector">
    <div class="row">
      <!-- Coluna Esquerda: Accordion com seleção -->
      <div :class="exibirMapaPermissoes ? 'col-md-7' : 'col-12'">
        <div v-if="!permissoesConsolidadas || permissoesConsolidadas.length === 0 || modulosAgrupados.length === 0" class="text-center text-muted small py-4">
          Nenhuma permissão de acesso disponível.
        </div>

        <div v-else class="accordion" :id="accordionId">
          <div v-for="(modulo, idx) in modulosAgrupados" :key="modulo.nome || idx" class="accordion-item">
            <h2 class="accordion-header d-flex" :id="'heading' + accordionId + idx">
              <button class="accordion-button collapsed" type="button" data-bs-toggle="collapse"
                :data-bs-target="'#collapse' + accordionId + idx" aria-expanded="false"
                :aria-controls="'collapse' + accordionId + idx">
                {{ modulo.nome || 'Geral' }}
              </button>
              <div class="d-flex align-items-center px-3 border-start" style="z-index: 2;">
                <div class="form-check form-switch mb-0" title="Selecionar todo este módulo">
                  <input class="form-check-input cursor-pointer" type="checkbox"
                    :checked="estadoModulo(modulo) === 'all'"
                    :ref="(el) => registrarRefModulo(modulo.nome, el)"
                    :indeterminate="estadoModulo(modulo) === 'some'"
                    @change="(e) => toggleModulo(modulo, e.target.checked)"
                    :disabled="disabled">
                </div>
              </div>
            </h2>

            <div :id="'collapse' + accordionId + idx" class="accordion-collapse collapse"
              :aria-labelledby="'heading' + accordionId + idx"
              :data-bs-parent="'#' + accordionId">
              <div class="accordion-body p-0">
                <div class="list-group list-group-flush" style="max-height: 400px; overflow-y: auto;">

                  <!-- Cada regra (menu) dentro do módulo -->
                  <div v-for="regra in modulo.regras" :key="regra.idRegraAcesso" class="list-group-item border-bottom">
                    <div class="d-flex align-items-center mb-1">
                      <div class="form-check form-switch mb-0 me-2" title="Selecionar todas as permissões desta regra">
                        <input class="form-check-input cursor-pointer" type="checkbox"
                          :checked="estadoRegra(modulo, regra) === 'all'"
                          :indeterminate="estadoRegra(modulo, regra) === 'some'"
                          @change="(e) => toggleRegra(modulo, regra, e.target.checked)"
                          :disabled="disabled">
                      </div>
                      <span class="fw-medium small">{{ regra.titulo || regra.chave }}</span>
                      <span class="text-body-secondary small ms-2">- {{ regra.chave }}</span>
                    </div>

                    <!-- Permissões individuais da regra -->
                    <div class="ps-4">
                      <template v-if="regra.permissoes && regra.permissoes.length > 0">
                          <div v-for="perm in regra.permissoes" :key="perm.idRegraAcessoPermissao" class="mb-1">
                            <label class="d-flex align-items-center cursor-pointer">
                              <input class="form-check-input me-2 mt-0" type="checkbox"
                                :checked="idsSelecionadosSet.has(perm.idRegraAcessoPermissao)"
                                @change="(e) => togglePermissao(perm.idRegraAcessoPermissao, e.target.checked)"
                                :disabled="disabled || isPermissaoHerdada(perm.idRegraAcessoPermissao)">
                              <span class="small" :class="{ 'text-body-secondary': isPermissaoHerdada(perm.idRegraAcessoPermissao) }">
                                <i v-if="isPermissaoHerdada(perm.idRegraAcessoPermissao)" class="bi bi-shield-lock me-1 text-warning" title="Permissão herdada de grupo"></i>
                                {{ perm.chave }}
                              </span>
                              <span class="text-body-secondary small ms-1" v-if="perm.descricao">- {{ perm.descricao }}</span>
                              <!-- Badge do grupo de origem (permissão herdada) -->
                              <span v-if="isPermissaoHerdada(perm.idRegraAcessoPermissao)"
                                class="badge badge-ghost-info ms-2 small" style="font-size: 0.65rem;">
                                <i class="bi bi-people me-1"></i>{{ nomeGrupoOrigem(perm.idRegraAcessoPermissao) }}
                              </span>
                            </label>

                          <!-- Controle de Expiração (exibido quando a permissão está selecionada) -->
                          <div v-if="exibirControleExpiracao && idsSelecionadosSet.has(perm.idRegraAcessoPermissao)"
                            class="ps-3 ms-1 py-1 border-start border-2 border-warning bg-warning bg-opacity-10 rounded-end d-flex flex-wrap align-items-center gap-3">
                            <div class="form-check form-switch mb-0">
                              <input class="form-check-input cursor-pointer" type="checkbox"
                                :id="'temp_' + perm.idRegraAcessoPermissao"
                                v-model="expiracaoMap[perm.idRegraAcessoPermissao].isTemporario"
                                :disabled="disabled">
                              <label class="form-check-label small cursor-pointer"
                                :for="'temp_' + perm.idRegraAcessoPermissao">Acesso Temporário?</label>
                            </div>
                            <div v-if="expiracaoMap[perm.idRegraAcessoPermissao]?.isTemporario" style="width: 250px;">
                              <div class="input-group input-group-sm">
                                <input type="date" class="form-control form-control-sm border-warning"
                                  v-model="expiracaoMap[perm.idRegraAcessoPermissao].dataExpiracao"
                                  :disabled="disabled">
                              </div>
                            </div>
                          </div>
                        </div>
                      </template>
                    </div>
                  </div>

                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Coluna Direita: Mapa de Permissões Selecionadas -->
      <div v-if="exibirMapaPermissoes" class="col-md-5">
        <h6 class="mb-3 mt-4 mt-md-0 border-bottom pb-2 d-flex justify-content-between">
          <span>Mapa de Permissões:</span>
          <span class="badge bg-primary rounded-pill">{{ totalSelecionadas }} Ativas</span>
        </h6>

        <div v-if="totalSelecionadas > 0"
          class="sticky-top bg-body-tertiary border border-secondary-subtle rounded p-3" style="top: 70px;">
          <div style="max-height: 500px; overflow-y: auto; overflow-x: hidden;">
            <div v-for="modulo in modulosAgrupados" :key="modulo.nome" class="mb-3">
              <div v-if="permissoesSelecionadasDoModulo(modulo).length > 0">
                <strong class="d-block mb-2 text-primary border-bottom pb-1 border-primary-subtle">
                  {{ modulo.nome }}:
                </strong>
                <ul class="list-unstyled ms-1 small mb-0 d-flex flex-column gap-2">
                  <li v-for="regra in modulo.regras" :key="regra.idRegraAcesso">
                    <div v-if="permissoesSelecionadasDaRegra(regra).length > 0" class="mb-2">
                      <span class="fw-medium text-body-secondary small d-block mb-1">
                        <i class="bi bi-chevron-right me-1"></i>{{ regra.titulo || regra.chave }}
                      </span>
                      <div v-for="perm in permissoesSelecionadasDaRegra(regra)" :key="perm.idRegraAcessoPermissao"
                        class="d-flex align-items-start ms-2 mb-1">
                        <i class="bi bi-check-circle-fill text-success mt-1 me-2"></i>
                        <div class="d-flex flex-column w-100">
                          <span class="text-body">{{ perm.chave }}</span>
                          <span v-if="expiracaoMap[perm.idRegraAcessoPermissao]?.isTemporario && expiracaoMap[perm.idRegraAcessoPermissao]?.dataExpiracao"
                            class="text-warning-emphasis bg-warning-subtle px-2 py-0 rounded mt-1 d-inline-block"
                            style="width: fit-content; font-size: 0.75rem;">
                            <i class="bi bi-hourglass-split"></i> Expira: {{ formatarDataLocal(expiracaoMap[perm.idRegraAcessoPermissao].dataExpiracao) }}
                          </span>
                        </div>
                      </div>
                    </div>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
        <div v-else class="text-muted text-center mt-3 p-4 bg-body-tertiary rounded border border-secondary-subtle">
          <i class="bi bi-shield-lock fs-2 d-block mb-2 text-secondary"></i>
          Nenhuma permissão selecionada.
        </div>
      </div>
    </div>

    <!-- Slot para ações do header (ex: botão "Liberar Todas") -->
    <div v-if="$slots['header-acoes']" class="d-flex justify-content-end mb-3">
      <slot name="header-acoes" />
    </div>
  </div>
</template>

<script setup>
import { ref, reactive, computed, watch, onMounted } from 'vue'

const props = defineProps({
  permissoesConsolidadas: { type: Array, required: true },
  modelValue: { type: Array, default: () => [] },
  permissoesHerdadas: { type: Array, default: () => [] },
  disabled: { type: Boolean, default: false },
  exibirControleExpiracao: { type: Boolean, default: false },
  exibirMapaPermissoes: { type: Boolean, default: true }
})

const emit = defineEmits(['update:modelValue', 'update:expiracao'])

// Mapa de permissões herdadas: id → nomeGrupo
const herdadasMap = computed(() => {
  const map = {}
  if (Array.isArray(props.permissoesHerdadas)) {
    for (const item of props.permissoesHerdadas) {
      if (item && item.idRegraAcessoPermissao) {
        map[item.idRegraAcessoPermissao] = item.nomeGrupo || 'Grupo'
      }
    }
  }
  return map
})

// Verifica se uma permissão é herdada (read-only)
function isPermissaoHerdada (idPermissao) {
  return idPermissao in herdadasMap.value
}

// Retorna o nome do grupo de origem de uma permissão herdada
function nomeGrupoOrigem (idPermissao) {
  return herdadasMap.value[idPermissao] || ''
}

// ID único para o accordion (evita conflito se houver múltiplos na página)
const accordionId = 'permSelector_' + Math.random().toString(36).substring(2, 8)

// Estado interno: inclui modelValue + IDs herdados (para exibição como checked)
const idsSelecionados = ref([])
const idsSelecionadosSet = computed(() => new Set(idsSelecionados.value))
const expiracaoMap = reactive({})
const moduloToggleRefs = ref({})

// Sincroniza modelValue + herdadas → idsSelecionados (com guarda anti-loop)
function sincronizarIds () {
  const base = [...props.modelValue]
  if (Array.isArray(props.permissoesHerdadas)) {
    for (const item of props.permissoesHerdadas) {
      if (item && item.idRegraAcessoPermissao && !base.includes(item.idRegraAcessoPermissao)) {
        base.push(item.idRegraAcessoPermissao)
      }
    }
  }
  // Guarda: só atualiza se realmente mudou (evita loop)
  const atual = idsSelecionados.value
  if (base.length !== atual.length || base.some((id, i) => id !== atual[i])) {
    idsSelecionados.value = base
  }
}

// Reage a mudanças externas (modelValue / herdadas) — com immediate para capturar o estado inicial
watch(() => props.modelValue, sincronizarIds, { deep: true, immediate: true })
watch(() => props.permissoesHerdadas, sincronizarIds, { deep: true })

// Os dados da API já vêm agrupados por módulo no formato:
// [{ modulo, iconeModulo, regras: [{ idRegraAcesso, chave, titulo, icone, permissoes }] }]
// Apenas normaliza os nomes dos campos para o template
const modulosAgrupados = computed(() => {
  if (!props.permissoesConsolidadas || !Array.isArray(props.permissoesConsolidadas)) return []
  return props.permissoesConsolidadas.map(mod => ({
    nome: mod.modulo || 'Geral',
    regras: (mod.regras || []).map(r => ({
      ...r,
      permissoes: (r.permissoes || []).filter(p => p != null)
    }))
  }))
})

// Inicializa expiracaoMap para todas as permissões
onMounted(() => {
  inicializarExpiracaoMap()
})

watch(() => props.permissoesConsolidadas, () => {
  inicializarExpiracaoMap()
}, { deep: true })

function inicializarExpiracaoMap () {
  if (!props.permissoesConsolidadas) return
  for (const item of props.permissoesConsolidadas) {
    if (!item || !item.regras) continue
    for (const regra of item.regras) {
      if (!regra || !regra.permissoes) continue
      for (const perm of regra.permissoes) {
        if (!perm || !perm.idRegraAcessoPermissao) continue
        if (!expiracaoMap[perm.idRegraAcessoPermissao]) {
          expiracaoMap[perm.idRegraAcessoPermissao] = {
            isTemporario: false,
            dataExpiracao: ''
          }
        }
      }
    }
  }
}

// Sincroniza idsSelecionados → modelValue (apenas IDs NÃO herdados)
// + garante entries no expiracaoMap
watch(idsSelecionados, () => {
  for (const id of idsSelecionados.value) {
    if (!expiracaoMap[id]) {
      expiracaoMap[id] = { isTemporario: false, dataExpiracao: '' }
    }
  }
  // Filtra IDs herdados para não enviá-los ao backend como permissões diretas
  const idsNaoHerdados = idsSelecionados.value.filter(id => !isPermissaoHerdada(id))
  emit('update:modelValue', [...idsNaoHerdados])
}, { deep: true, immediate: true })

// Total de permissões selecionadas
const totalSelecionadas = computed(() => idsSelecionadosSet.value.size)

// Registra refs dos toggles de módulo (para indeterminate)
function registrarRefModulo (modulo, el) {
  if (el) moduloToggleRefs.value[modulo] = el
}

// Estado do módulo: 'none' | 'some' | 'all'
function estadoModulo (modulo) {
  const todasPermissoes = (modulo.regras || []).flatMap(r => (r.permissoes || []).filter(p => p != null))
  if (todasPermissoes.length === 0) return 'none'
  const selecionadas = todasPermissoes.filter(p => p && idsSelecionadosSet.value.has(p.idRegraAcessoPermissao))
  if (selecionadas.length === 0) return 'none'
  if (selecionadas.length === todasPermissoes.length) return 'all'
  return 'some'
}

// Estado da regra: 'none' | 'some' | 'all'
function estadoRegra (modulo, regra) {
  const permissoes = (regra.permissoes || []).filter(p => p != null)
  if (permissoes.length === 0) return 'none'
  const selecionadas = permissoes.filter(p => p && idsSelecionadosSet.value.has(p.idRegraAcessoPermissao))
  if (selecionadas.length === 0) return 'none'
  if (selecionadas.length === permissoes.length) return 'all'
  return 'some'
}

// Toggle módulo: seleciona/desseleciona todas as permissões de todas as regras do módulo
function toggleModulo (modulo, checked) {
  const todasPermissoes = (modulo.regras || []).flatMap(r => r.permissoes || [])
  const ids = todasPermissoes.map(p => p.idRegraAcessoPermissao)
  if (checked) {
    ids.forEach(id => {
      if (!idsSelecionadosSet.value.has(id)) idsSelecionados.value.push(id)
    })
  } else {
    idsSelecionados.value = idsSelecionados.value.filter(id => !ids.includes(id))
  }
}

// Toggle regra: seleciona/desseleciona todas as permissões da regra
function toggleRegra (modulo, regra, checked) {
  const ids = (regra.permissoes || []).filter(p => p != null).map(p => p.idRegraAcessoPermissao)
  if (checked) {
    ids.forEach(id => {
      if (!idsSelecionadosSet.value.has(id)) idsSelecionados.value.push(id)
    })
  } else {
    idsSelecionados.value = idsSelecionados.value.filter(id => !ids.includes(id))
  }
}

// Toggle permissão individual
function togglePermissao (idPermissao, checked) {
  if (checked) {
    if (!idsSelecionadosSet.value.has(idPermissao)) idsSelecionados.value.push(idPermissao)
  } else {
    idsSelecionados.value = idsSelecionados.value.filter(id => id !== idPermissao)
  }
}

// Permissões selecionadas de um módulo (para o mapa)
function permissoesSelecionadasDoModulo (modulo) {
  return (modulo.regras || []).flatMap(r => (r.permissoes || []).filter(p => p != null && idsSelecionadosSet.value.has(p.idRegraAcessoPermissao)))
}

// Permissões selecionadas de uma regra (para o mapa)
function permissoesSelecionadasDaRegra (regra) {
  return (regra.permissoes || []).filter(p => p != null && idsSelecionadosSet.value.has(p.idRegraAcessoPermissao))
}

// Formata data para exibição no mapa
function formatarDataLocal (dataIso) {
  if (!dataIso) return ''
  try {
    const [ano, mes, dia] = dataIso.split('-')
    return `${dia}/${mes}/${ano}`
  } catch {
    return dataIso
  }
}
</script>

<style scoped>
.cursor-pointer {
  cursor: pointer;
}
.accordion-body .list-group-item {
  border: none;
  padding-top: 0.5rem;
  padding-bottom: 0.5rem;
  background-color: transparent;
}
</style>
